import { useEffect, useId, useState } from 'react'
import toast from 'react-hot-toast'
import { Link } from 'react-router-dom'
import { ShoppingBagIcon, StarIcon, XMarkIcon, CreditCardIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import { marketplaceAPI, monetizationAPI } from '../services/api'
import { completeCardPayment } from '../services/cardPayment'
import type { SignalPackage, PaymentMethod } from '../types'
import CourseShelf from '../components/courses/CourseShelf'
import LoadingScreen from '../components/LoadingScreen'
import Tooltip from '../components/Tooltip'
import { MARKETPLACE_GLOSSARY } from '../config/glossary'
import { creatorRecordPath } from '../routes'
import { mediaUrl } from '../utils/mediaUrl'

// ---- Buy Modal ----
interface BuyModalProps {
  pkg: SignalPackage
  onClose: () => void
  onPurchased: (pkgId: number) => void
}

const BuyModal = ({ pkg, onClose, onPurchased }: BuyModalProps) => {
  const titleId = useId()
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [selectedMethodId, setSelectedMethodId] = useState<string | null>(null)
  const [isLoadingMethods, setIsLoadingMethods] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    setIsLoadingMethods(true)
    monetizationAPI.getPaymentMethods()
      .then((methods: PaymentMethod[]) => {
        setPaymentMethods(methods)
        const def = methods.find((m) => m.is_default) || methods[0]
        setSelectedMethodId(def ? def.id : null)
      })
      .catch(() => toast.error('Failed to load payment methods'))
      .finally(() => setIsLoadingMethods(false))
  }, [])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [isSubmitting, onClose])

  const handlePurchase = async () => {
    if (!selectedMethodId) { toast.error('Select a payment method'); return }
    setIsSubmitting(true)
    try {
      const charge = await marketplaceAPI.purchase(pkg.id, selectedMethodId)
      // 3-D Secure: the bank's approval step, when the card asks for one.
      const outcome = await completeCardPayment(charge)
      if (outcome.status === 'failed') {
        toast.error(outcome.message)
        return
      }
      toast.success(
        charge?.payment_status === 'requires_action'
          ? `Approved. "${pkg.title}" opens in a moment.`
          : `Purchased "${pkg.title}"!`
      )
      onPurchased(pkg.id)
      onClose()
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Purchase failed')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 id={titleId} className="text-lg font-bold text-gray-900">Purchase Package</h2>
          <button onClick={onClose} disabled={isSubmitting} className="text-gray-500 hover:text-gray-700">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <div className="p-6 space-y-5">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="font-semibold text-gray-900">{pkg.title}</p>
            <p className="text-sm text-gray-500 mt-1">{pkg.short_description}</p>
            <p className="mt-2 text-2xl font-bold text-gray-900">
              ${(pkg.price_cents / 100).toFixed(2)}
              <span className="text-sm font-normal text-gray-500 ml-1">{pkg.currency || 'USD'}</span>
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
            {isLoadingMethods ? (
              <p className="text-sm text-gray-500">Loading payment methods...</p>
            ) : paymentMethods.length === 0 ? (
              <p className="text-sm text-gray-500">No payment methods on file. Add one in Monetization settings.</p>
            ) : (
              <div className="space-y-2">
                {paymentMethods.map((method) => (
                  <label
                    key={method.id}
                    className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedMethodId === method.id
                        ? 'border-primary-500 ring-1 ring-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="buy-payment-method"
                      value={method.id}
                      checked={selectedMethodId === method.id}
                      onChange={() => setSelectedMethodId(method.id)}
                      className="h-4 w-4 text-primary-600 border-gray-300"
                    />
                    <CreditCardIcon className="h-5 w-5 text-gray-600 flex-shrink-0" />
                    <span className="text-sm font-medium text-gray-900">
                      {method.brand || 'Card'} ****{method.last4}
                    </span>
                    {method.is_default && (
                      <span className="inline-flex items-center text-xs text-success-600 ml-auto">
                        <CheckCircleIcon className="h-3 w-3 mr-1" />
                        Default
                      </span>
                    )}
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-2 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handlePurchase}
              disabled={isSubmitting || !selectedMethodId}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 font-medium disabled:opacity-50"
            >
              {isSubmitting ? 'Purchasing...' : `Buy $${(pkg.price_cents / 100).toFixed(2)}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ---- Star Rating ----
const StarRating = ({ rating, count }: { rating: number; count?: number }) => (
  <div className="flex items-center gap-1">
    {[1, 2, 3, 4, 5].map((star) => (
      <StarIcon
        key={star}
        className={`h-4 w-4 ${star <= Math.round(rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
      />
    ))}
    {count != null && <span className="text-xs text-gray-500 ml-1">({count})</span>}
  </div>
)

// ---- Package Card ----
interface PackageCardProps {
  pkg: SignalPackage
  isPurchased: boolean
  onBuy: (pkg: SignalPackage) => void
}

const PackageCard = ({ pkg, isPurchased, onBuy }: PackageCardProps) => {
  const [expanded, setExpanded] = useState(false)

  const difficultyColors: Record<string, string> = {
    beginner: 'bg-success-50 text-success-700',
    intermediate: 'bg-yellow-50 text-yellow-700',
    advanced: 'bg-danger-50 text-danger-700',
  }

  return (
    <div className="bg-white rounded-lg shadow hover:shadow-md transition-shadow">
      {pkg.cover_image_url && (
        <img
          src={mediaUrl(pkg.cover_image_url)}
          alt={pkg.title}
          className="w-full h-40 object-cover rounded-t-lg"
        />
      )}
      <div className="p-5">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="text-base font-semibold text-gray-900 leading-snug">{pkg.title}</h3>
          {pkg.is_featured && (
            <span className="shrink-0 text-xs bg-primary-50 text-primary-700 px-2 py-0.5 rounded font-medium">
              Featured
            </span>
          )}
        </div>

        {pkg.creator_username && (
          <p className="text-xs text-gray-500 mb-2">
            by @{pkg.creator_username} ·{' '}
            {/* No figures for the package itself: a set of signals someone picked isn't their record. */}
            <Link to={creatorRecordPath(pkg.creator_username)} className="text-primary-600 hover:underline">
              See their full record
            </Link>
          </p>
        )}

        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{pkg.short_description}</p>

        <div className="flex flex-wrap gap-2 mb-3">
          {pkg.difficulty_level && (
            <span className={`text-xs px-2 py-0.5 rounded font-medium ${difficultyColors[pkg.difficulty_level.toLowerCase()] || 'bg-gray-100 text-gray-700'}`}>
              {pkg.difficulty_level}
            </span>
          )}
          <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded font-medium">
            {pkg.total_signals} signals
          </span>
        </div>

        {pkg.average_rating != null && pkg.total_reviews != null && pkg.total_reviews > 0 && (
          <div className="mb-3">
            <StarRating rating={pkg.average_rating} count={pkg.total_reviews} />
          </div>
        )}

        <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-100">
          <span className="text-lg font-bold text-gray-900">
            ${(pkg.price_cents / 100).toFixed(2)}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setExpanded((v) => !v)}
              className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50 transition-colors"
            >
              {expanded ? 'Hide' : 'View'}
            </button>
            {isPurchased ? (
              <span className="px-3 py-1.5 bg-success-50 text-success-700 text-sm rounded-md font-medium flex items-center gap-1">
                <CheckCircleIcon className="h-4 w-4" />
                Purchased
              </span>
            ) : (
              <button
                onClick={() => onBuy(pkg)}
                className="px-3 py-1.5 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 transition-colors font-medium"
              >
                Buy
              </button>
            )}
          </div>
        </div>

        {expanded && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            {pkg.description ? (
              <p className="text-sm text-gray-600 leading-relaxed">{pkg.description}</p>
            ) : (
              <p className="text-sm text-gray-400 italic">{pkg.short_description}</p>
            )}
            {pkg.tags && pkg.tags.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1">
                {pkg.tags.map((tag) => (
                  <span key={tag} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ---- Main Page ----
const Marketplace = () => {
  const [packages, setPackages] = useState<SignalPackage[]>([])
  const [purchasedIds, setPurchasedIds] = useState<Set<number>>(new Set())
  const [isLoading, setIsLoading] = useState(true)
  const [buyTarget, setBuyTarget] = useState<SignalPackage | null>(null)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setIsLoading(true)
    try {
      const [pkgs, purchased] = await Promise.all([
        marketplaceAPI.search({}),
        marketplaceAPI.getPurchased().catch(() => []),
      ])
      setPackages(Array.isArray(pkgs?.packages) ? pkgs.packages : [])
      const ids = new Set<number>((Array.isArray(purchased) ? purchased : []).map((p: any) => p.id ?? p.package_id))
      setPurchasedIds(ids)
    } catch (err: any) {
      toast.error('Failed to load marketplace')
    } finally {
      setIsLoading(false)
    }
  }

  const handlePurchased = (pkgId: number) => {
    setPurchasedIds((prev) => new Set([...prev, pkgId]))
  }

  if (isLoading) return <LoadingScreen />

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            <Tooltip content={MARKETPLACE_GLOSSARY.signalPackages}>Signal Packages</Tooltip> Marketplace
          </h1>
          <p className="text-gray-500 mt-1">Browse and purchase curated signal packages from top creators</p>
        </div>
      </div>

      {packages.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <ShoppingBagIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg font-medium">No packages available yet</p>
          <p className="text-gray-400 mt-1">Check back soon for new signal packages from creators.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {packages.map((pkg) => (
            <PackageCard
              key={pkg.id}
              pkg={pkg}
              isPurchased={purchasedIds.has(pkg.id)}
              onBuy={setBuyTarget}
            />
          ))}
        </div>
      )}

      <CourseShelf />

      {buyTarget && (
        <BuyModal
          pkg={buyTarget}
          onClose={() => setBuyTarget(null)}
          onPurchased={handlePurchased}
        />
      )}
    </div>
  )
}

export default Marketplace
